type Phase = 'idle' | 'waiting' | 'writing';
type Reply = { status: string; revision: number; messages: string[]; delays: number[]; delivered: number; retryAfterMs?: number };
type Callbacks = { user: (text: string) => void; assistant: (text: string) => void; phase: (phase: Phase) => void; error: (text: string) => void; remaining: (turns: number) => void; handoff: (url: string) => void };

export class DemoConversation {
  private disposed = false;
  private token = '';
  private tokenRequest: Promise<string> | null = null;
  private chain: Promise<void> = Promise.resolve();
  private outbox: { id: string; text: string }[] = [];
  private draft = '';
  private epoch = 0;
  private lastActivity = 0;
  private lastNotice = 0;
  private working = false;
  private work = false;
  private closed = false;
  private timer: ReturnType<typeof setTimeout> | undefined;
  private notice: ReturnType<typeof setTimeout> | undefined;
  private failedDelivery: { reply: Reply; index: number } | null = null;
  private shown = new Set<string>();
  private abort = new AbortController();
  constructor(private path: string, private quietMs: number, private callbacks: Callbacks) {}
  private async api(action: string, body: object) {
    const controller = new AbortController();
    const cancel = () => controller.abort(); this.abort.signal.addEventListener('abort', cancel, { once: true });
    const timeout = setTimeout(cancel, 40000);
    try {
      const response = await fetch(this.path + action, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body), signal: controller.signal });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'A assistente está indisponível agora.');
      return data;
    } finally { clearTimeout(timeout); this.abort.signal.removeEventListener('abort', cancel); }
  }
  private async session() {
    if (this.token) return this.token;
    if (!this.tokenRequest) this.tokenRequest = this.api('/session', {}).then(data => this.token = data.sessionToken).finally(() => { this.tokenRequest = null; });
    return this.tokenRequest;
  }
  private serial(task: () => Promise<void>) {
    this.chain = this.chain.then(async () => { if (!this.disposed) await task(); }).catch(error => this.report(error));
    return this.chain;
  }
  private report(error: Error) { if (!this.disposed) { clearTimeout(this.timer); this.callbacks.phase('idle'); this.callbacks.error(error.name === 'AbortError' ? 'A resposta demorou. Tente novamente em instantes.' : error.message); } }
  private async flush() {
    const token = await this.session();
    while (this.outbox.length && !this.disposed) {
      const item = this.outbox[0]; await this.api('/events', { sessionToken: token, requestId: item.id, message: item.text });
      this.outbox.shift();
    }
  }
  send(text: string) {
    if (this.disposed || this.closed || !text.trim()) return;
    this.outbox.push({ id: crypto.randomUUID(), text: text.trim() }); this.draft = ''; this.work = true; this.failedDelivery = null;
    this.callbacks.user(text.trim()); this.callbacks.error(''); this.interrupt();
    this.serial(() => this.flush()).then(() => this.schedule());
  }
  activity(text: string) {
    if (this.disposed || this.closed) return;
    this.draft = text; this.interrupt();
    if (!this.work && !this.token) return;
    clearTimeout(this.notice);
    const notify = () => {
      this.lastNotice = Date.now();
      this.serial(async () => { const token = await this.session(); await this.api('/activity', { sessionToken: token, typing: Boolean(this.draft.trim()) }); }).then(() => this.schedule());
    };
    if (Date.now() - this.lastNotice >= 500) notify(); else this.notice = setTimeout(notify, 500);
    this.schedule();
  }
  private interrupt() { this.epoch++; this.lastActivity = Date.now(); clearTimeout(this.timer); if (this.work) this.callbacks.phase('waiting'); }
  private schedule(delay = Math.max(0, this.quietMs - (Date.now() - this.lastActivity))) {
    clearTimeout(this.timer);
    if (this.disposed || this.closed || !this.work || this.draft.trim()) return;
    this.timer = setTimeout(() => this.poll(), Math.max(50, delay));
  }
  private async poll() {
    if (this.disposed || this.draft.trim() || !this.work) return;
    if (this.working) { this.schedule(500); return; }
    this.working = true; const epoch = this.epoch;
    try {
      await this.chain;
      if (this.disposed || this.draft.trim() || epoch !== this.epoch || this.outbox.length) return;
      this.callbacks.phase('writing');
      const reply: Reply = await this.api('/reply', { sessionToken: await this.session() });
      if (this.disposed || epoch !== this.epoch) return;
      if (reply.status === 'reply') { this.deliver(reply, reply.delivered, epoch); }
      else if (reply.status === 'idle') { this.work = false; this.callbacks.phase('idle'); }
      else { this.callbacks.phase('waiting'); this.schedule(reply.retryAfterMs || 1000); }
    } catch (error) { if (epoch === this.epoch) this.report(error as Error); }
    finally { this.working = false; if (epoch !== this.epoch) this.schedule(); }
  }
  private deliver(reply: Reply, index: number, epoch: number) {
    if (this.disposed || epoch !== this.epoch || this.draft.trim()) return;
    if (index >= reply.messages.length) { this.work = false; this.callbacks.phase('idle'); return; }
    this.callbacks.phase('writing');
    this.timer = setTimeout(async () => {
      if (this.disposed || epoch !== this.epoch || this.draft.trim()) return;
      try {
        const result = await this.api('/delivered', { sessionToken: this.token, revision: reply.revision, index });
        if (this.disposed) return;
        if (result.stale) { this.callbacks.phase('waiting'); this.schedule(); return; }
        const id = `${reply.revision}:${index}`;
        if (!this.shown.has(id)) { this.shown.add(id); this.callbacks.assistant(result.message); }
        this.callbacks.remaining(result.remainingTurns); this.failedDelivery = null;
        if (result.handoff) { this.closed = true; this.work = false; this.callbacks.handoff(result.contactUrl || ''); this.callbacks.phase('idle'); return; }
        if (epoch === this.epoch) this.deliver(reply, index + 1, epoch);
      } catch (error) { if (epoch === this.epoch) { this.failedDelivery = { reply, index }; this.report(error as Error); } }
    }, Math.max(1600, Math.min(4500, reply.delays[index] || 1600)));
  }
  retry() {
    if (this.disposed || this.closed) return;
    this.callbacks.error('');
    if (this.failedDelivery) { this.deliver(this.failedDelivery.reply, this.failedDelivery.index, this.epoch); return; }
    this.work = true; this.serial(() => this.flush()).then(() => this.schedule());
  }
  dispose() { this.disposed = true; this.abort.abort(); clearTimeout(this.timer); clearTimeout(this.notice); }
}
